/**
 * Results tab (FR-4.2/4.3, DC-1794 demo D4) — the run, read back.
 *
 * The honesty rules are the layout:
 *
 *  - the counts come FIRST, before any finding, and `not assessable` and
 *    `attested` are stated in that band rather than left to be inferred from
 *    a list (engine doc §6 — a results view that implies full coverage is a
 *    lie the UI tells for us);
 *  - `marginal` is its own outcome with its own tone. It is never rendered
 *    as a pass or a fail;
 *  - a `not_assessable` finding drills down to an empty state that NAMES what
 *    would answer it, never a blank panel (FR-4.3 AC fail);
 *  - evidence that was present and did not count is shown WITH its reason —
 *    an expired audit is more interesting than a missing one;
 *  - the run's catalog version and digest are on the page, and a stale run
 *    says so.
 *
 * Compass computes nothing here: every number is read from the immutable run.
 *
 * ARC IP boundary: identifiers plus catalog content the API already serves
 * under the organization's entitlement. Nothing is embedded in this file.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { usePermissions } from '../../hooks/useAuth';
import {
  CONFIDENCE_LABEL,
  DISREGARD_LABEL,
  LINE_LABEL,
  OUTCOME_VOCABULARY,
  type RunFinding,
  type RunResults,
} from './assessmentTypes';
import { ControlGrid } from './ControlGrid';
import { CoverageStrip } from './CoverageStrip';
import { FindingChip } from './FindingChip';
import { contradictedByOf, isDisputed } from './findingDispute';

interface ResultsEnvelope {
  data?: { results?: RunResults };
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'no_run' }
  | { phase: 'ready'; results: RunResults };

interface Disregarded {
  itemId: string;
  kind: string;
  reason: string;
  reliabilityReason?: string;
  expiresAt?: string;
}

function disregardedOf(finding: RunFinding): Disregarded[] {
  const raw = finding.detail?.disregarded;
  return Array.isArray(raw) ? (raw as Disregarded[]) : [];
}

// DC-1799 dispute identification moved to `./findingDispute` (DC-1806) so
// Results and the Decision tab share one rule.

function absenceReason(finding: RunFinding): string | null {
  const reason = finding.detail?.reason;
  return typeof reason === 'string' ? reason : null;
}

const ABSENCE_COPY: Record<string, string> = {
  no_evidence: 'Nothing on the record answers this test yet.',
  spec_unparsed:
    'This test’s evaluation is documented but not yet machine-wired, so a person has to answer it.',
  spec_categorical:
    'This test evaluates a categorical input, which no collector reads in this cut — a person has to answer it.',
  catalog_test_absent:
    'This test is not in the catalog version your organization is entitled to read.',
};

function EvidenceLine({ item }: { item: RunFinding['evidence'][number] }): React.ReactElement {
  return (
    <li className="uc-results-evidence-item">
      <span className="uc-results-evidence-kind">{item.kind.replace(/_/g, ' ')}</span>
      <span className="uc-results-evidence-label">{item.label}</span>
      {item.interview && (
        <>
          <q className="uc-results-quote">{item.interview.quote}</q>
          <span className="uc-results-evidence-meta">
            {item.interview.speaker} · {item.interview.stance}
            {item.interview.confirmed ? ' · confirmed' : ' · candidate (not evidence)'}
          </span>
        </>
      )}
      {item.external && (
        <span className="uc-results-evidence-meta">
          {item.external.issuer} · as of {item.external.asOfDate} · expires {item.external.expiresAt}
          {item.external.expired && <strong className="uc-results-flag"> expired</strong>}
          {item.external.reliability === 'not_relied_upon' && (
            <strong className="uc-results-flag">
              {' '}
              not relied upon
              {item.external.reliabilityReason ? ` — ${item.external.reliabilityReason}` : ''}
            </strong>
          )}
        </span>
      )}
      {item.system && (
        <span className="uc-results-evidence-meta">
          {item.system.systemClass} · attributes: {item.system.attributesAsserted.join(', ')}
        </span>
      )}
    </li>
  );
}

function Finding({
  finding,
  spotlight,
}: {
  finding: RunFinding;
  /** Set by a grid-segment activation: open this finding and bring it into view. */
  spotlight: number;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const headRef = React.useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (spotlight > 0) {
      setOpen(true);
      headRef.current?.focus();
      // Optional-called: jsdom has no scrollIntoView, and a missing scroll
      // must never break the open.
      headRef.current?.scrollIntoView?.({ block: 'center' });
    }
  }, [spotlight]);
  const vocabulary = OUTCOME_VOCABULARY[finding.outcome];
  const disregarded = disregardedOf(finding);
  const reason = absenceReason(finding);
  const disputed = isDisputed(finding);
  const contradictedIds = new Set(contradictedByOf(finding).map((ref) => ref.itemId));
  const disputingEvidence = finding.evidence.filter((item) => contradictedIds.has(item.itemId));

  return (
    <li className="uc-results-finding" data-testid={`finding-${finding.testCode}`}>
      <button
        ref={headRef}
        type="button"
        className="uc-results-finding-head"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="uc-results-test-code">{finding.testCode}</span>
        {/* DC-1807: the catalog name beside the identifier — entitlement-joined,
            absent when unreadable, never a blank placeholder. */}
        {finding.testName && (
          <span className="uc-results-test-name" data-testid={`test-name-${finding.testCode}`}>
            {finding.testName}
          </span>
        )}
        <span className="uc-results-test-control">{finding.controlCode}</span>
        {/* Results v2 §2+§3: fill = answered, texture = basis, colour+label =
            outcome. An impossible state throws in dev, renders open in prod. */}
        <FindingChip finding={finding} />
        {/* DC-1799 AC6: a banded outcome a person disputes is flagged on the
            row itself — never only inside the drill-down. */}
        {disputed && (
          <span className="uc-results-disputed-flag" data-testid={`disputed-${finding.testCode}`}>
            disputed
          </span>
        )}
        {/* DC-1805: the independence overlay in the same idiom — a quiet
            marker, never a matrix change, never a block or a discount. */}
        {finding.accountability?.selfAttestedOnly && (
          <span
            className="uc-results-selfattested-flag"
            data-testid={`selfattested-${finding.testCode}`}
          >
            first-line only
          </span>
        )}
        {finding.band !== null && (
          <span className="uc-results-score" data-testid={`score-${finding.testCode}`}>
            {finding.band} · {finding.score}
          </span>
        )}
        <span className="uc-results-confidence">{CONFIDENCE_LABEL[finding.confidence]}</span>
        <span className="uc-results-disclosure" aria-hidden="true">
          {open ? '−' : '+'}
        </span>
      </button>

      {open && (
        <div className="uc-results-drill">
          <p className="uc-results-meaning">{vocabulary.meaning}</p>

          {/* DC-1807: where this test lives — its control by name, and the
              path back to the library for a reader who has lost the thread. */}
          <p className="uc-results-context" data-testid={`context-${finding.testCode}`}>
            Control {finding.controlCode}
            {finding.controlName ? ` — ${finding.controlName}` : ''}
            {' · '}
            <Link
              to={`/arc-library?control=${encodeURIComponent(finding.controlCode)}&test=${encodeURIComponent(finding.testCode)}`}
            >
              View in ARC Library
            </Link>
          </p>

          {disputed && (
            <div
              className="uc-results-disputed"
              role="note"
              data-testid={`disputed-detail-${finding.testCode}`}
            >
              <h4>Disputed by a confirmed attestation</h4>
              <p className="uc-results-disputed-meaning">
                The{' '}
                {finding.detail?.basis === 'data_collector'
                  ? 'measured'
                  : 'manually entered'}{' '}
                outcome stands — a confirmed attestation is retained beside it because it
                contradicts this control, and a reviewer decides what that means.
              </p>
              {disputingEvidence.length > 0 && (
                <ul>
                  {disputingEvidence.map((item) => (
                    <li key={item.itemId}>
                      {item.interview ? (
                        <>
                          <q className="uc-results-quote">{item.interview.quote}</q>{' '}
                          <span className="uc-results-evidence-meta">
                            — {item.interview.speaker}
                          </span>
                        </>
                      ) : (
                        <span className="uc-results-evidence-label">{item.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* DC-1805 drill-down: who supplied the answer, and which lines.
              Stated only for attested findings — measured findings name
              their collector in the provenance line, and absent findings
              have no supplier to state. */}
          {finding.confidence === 'attested' && finding.accountability && (
            <div
              className="uc-results-accountability"
              role="note"
              data-testid={`accountability-${finding.testCode}`}
            >
              {finding.accountability.selfAttestedOnly && (
                <p className="uc-results-accountability-flag">
                  Every satisfying attestation on this finding comes from the first line — the
                  party that owns the risk certified its own control. The outcome stands
                  undiscounted; a reviewer decides what the arrangement means.
                </p>
              )}
              {finding.accountability.suppliedBy.length > 0 && (
                <p className="uc-results-accountability-suppliers">
                  Supplied by{' '}
                  {finding.accountability.suppliedBy
                    .map(
                      (actor) =>
                        `${actor.displayName} (${actor.roleTitle} · ${LINE_LABEL[actor.line]})`
                    )
                    .join('; ')}
                  .
                </p>
              )}
              {finding.accountability.unattributed && (
                <p className="uc-results-accountability-unstated">
                  Line unstated — {finding.accountability.suppliedBy.length > 0 ? 'some of ' : ''}
                  the satisfying evidence is not attributed to a registered actor, so no
                  independence claim is made for it in either direction.
                </p>
              )}
            </div>
          )}

          {finding.evidence.length > 0 ? (
            <ul className="uc-results-evidence-list">
              {finding.evidence.map((item) => (
                <EvidenceLine key={item.itemId} item={item} />
              ))}
            </ul>
          ) : (
            // FR-4.3 AC fail: never a blank panel. Name the gap.
            <div className="uc-results-empty" data-testid={`empty-${finding.testCode}`}>
              <p className="uc-results-empty-reason">
                {(reason && ABSENCE_COPY[reason]) ?? 'Nothing on the record answers this test yet.'}
              </p>
              {finding.expects ? (
                <p className="uc-results-expects">
                  <span className="uc-results-expects-label">What would answer it:</span>{' '}
                  {finding.expects}
                </p>
              ) : (
                <p className="uc-results-expects uc-results-expects-unknown">
                  The catalog description for this test is not readable here, so what would answer
                  it cannot be stated.
                </p>
              )}
            </div>
          )}

          {disregarded.length > 0 && (
            <div className="uc-results-disregarded">
              <h4>Present, and not counted</h4>
              <ul>
                {disregarded.map((item) => (
                  <li key={`${item.itemId}-${item.reason}`}>
                    <span className="uc-results-evidence-kind">
                      {item.kind.replace(/_/g, ' ')}
                    </span>{' '}
                    — {DISREGARD_LABEL[item.reason] ?? item.reason}
                    {item.reliabilityReason ? ` (${item.reliabilityReason})` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="uc-results-provenance">
            Produced by {finding.collectorId} v{finding.collectorVersion}
            {finding.leg ? ` · ${finding.leg} channel` : ' · unrouted'} · observed{' '}
            {new Date(finding.observedAt).toLocaleString()}
          </p>
        </div>
      )}
    </li>
  );
}

export const UseCaseResults: React.FC<{ useCaseId: string; onRan?: () => void }> = ({
  useCaseId,
  onRan,
}) => {
  const { get, post } = useApiClient();
  // The same gate the server enforces (requireAnalystOrAdmin) — role, not
  // journey/dataset permission, so the surface cannot drift from the route.
  const { isAdmin, isAnalyst } = usePermissions();
  const canEdit = isAdmin || isAnalyst;
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [running, setRunning] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  // §5: the card is the click target for filtering the finding list.
  const [activeControl, setActiveControl] = useState<string | null>(null);
  // A monotonically increasing nonce per test so re-activating the same
  // segment re-opens and re-scrolls the row.
  const [spotlights, setSpotlights] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const res = await get<ResultsEnvelope>(`/api/use-cases/${useCaseId}/assessment-runs/latest`);
      const results = res.data?.results;
      setState(results ? { phase: 'ready', results } : { phase: 'no_run' });
    } catch (error) {
      const status = (error as { status?: number }).status;
      setState(status === 404 ? { phase: 'no_run' } : { phase: 'error' });
    }
  }, [get, useCaseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(async () => {
    setRunning(true);
    setRefusal(null);
    try {
      await post(`/api/use-cases/${useCaseId}/assessment-runs`, {});
      await load();
      onRan?.();
    } catch (error) {
      const body = (error as { body?: { error?: { message?: string } } }).body;
      setRefusal(
        body?.error?.message ??
          'The run was refused. Nothing was recorded — the refusal is the honest result.'
      );
    } finally {
      setRunning(false);
    }
  }, [post, useCaseId, load, onRan]);

  if (state.phase === 'loading') {
    return <div className="uc-results" aria-busy="true" />;
  }

  if (state.phase === 'error') {
    return (
      <div className="uc-results">
        <div className="banking-grid-error" role="alert">
          <p>The assessment results could not be read, so this tab states nothing about them.</p>
          <button type="button" className="banking-btn banking-btn-secondary" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (state.phase === 'no_run') {
    return (
      <section className="uc-results" aria-label="Results">
        <p className="uc-results-lede">
          No assessment has run for this use case. Nothing below is a measurement of zero — there is
          simply no run to read.
        </p>
        {/* §7: the strip renders fully open — never zeroed, never a spinner. */}
        <CoverageStrip counts={null} />
        {canEdit && (
          <button
            type="button"
            className="banking-btn banking-btn-primary"
            data-testid="run-assessment"
            disabled={running}
            onClick={() => void run()}
          >
            {running ? 'Running…' : 'Run assessment'}
          </button>
        )}
        {refusal && (
          <p className="uc-results-refusal" role="alert">
            {refusal}
          </p>
        )}
      </section>
    );
  }

  const { run: header, counts, findings, rollup } = state.results;
  const visibleFindings =
    activeControl === null
      ? findings
      : findings.filter((finding) => finding.controlCode === activeControl);

  return (
    <section className="uc-results" aria-label="Results">
      <header className="uc-results-header">
        <div>
          <h3 className="uc-results-title">
            Run of {new Date(header.createdAt).toLocaleString()}
          </h3>
          <p className="uc-results-provenance">
            Catalog {header.catalogVersionLabel ?? '—'} · digest{' '}
            <code>{header.catalogContentDigest.slice(0, 12)}…</code> · engine {header.engineVersion}{' '}
            · run by {header.createdByName}
          </p>
        </div>
        {canEdit && (
          <button
            type="button"
            className="banking-btn banking-btn-secondary"
            data-testid="run-assessment"
            disabled={running}
            onClick={() => void run()}
          >
            {running ? 'Running…' : 'Re-assess (new run)'}
          </button>
        )}
      </header>

      {header.stale && (
        <p className="uc-results-stale" data-testid="run-stale">
          This run was judged against a catalog your organization no longer holds. It remains valid
          for what it judged — re-assess to measure against the current baseline.
        </p>
      )}

      {refusal && (
        <p className="uc-results-refusal" role="alert">
          {refusal}
        </p>
      )}

      {/* FR-4.2 → Results v2 §4: coverage before any detail. The strip is
          count-based, states the collector_error reliability gap the old
          counts row omitted, and carries the ARC band (DC-1746) in the same
          container so the two cannot be separated. Still no percentage and
          no gauge: the band is a 0-5 score and a name. */}
      <CoverageStrip counts={counts} rollup={rollup} />

      {/* Results v2 §5: control cards above the finding list — two levels
          only (control → test), sorted by unanswered desc → not_satisfied
          desc → controlCode. The list below remains the drill layer. */}
      <ControlGrid
        findings={findings}
        activeControl={activeControl}
        onToggleControl={(controlCode) =>
          setActiveControl((current) => (current === controlCode ? null : controlCode))
        }
        onOpenFinding={(testCode) => {
          const owner = findings.find((finding) => finding.testCode === testCode);
          // A segment activation always reveals its finding: clear a filter
          // that would hide it, then spotlight the row.
          if (owner && activeControl !== null && owner.controlCode !== activeControl) {
            setActiveControl(null);
          }
          setSpotlights((current) => ({ ...current, [testCode]: (current[testCode] ?? 0) + 1 }));
        }}
      />

      <ul className="uc-results-findings">
        {visibleFindings.map((finding) => (
          <Finding
            key={finding.id}
            finding={finding}
            spotlight={spotlights[finding.testCode] ?? 0}
          />
        ))}
      </ul>
    </section>
  );
};

export default UseCaseResults;
